import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    'Relocation & Expatriation Services',
    'Business Setup & Corporate Services',
    'Financial, Tax & Compliance Consulting',
    'SAM Incubator',
    'SAM Talent & Solutions',
    'General Inquiry',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Main Office',
      content: 'Office no 08-149, We Work, One Central\nDubai World Trade Centre\nDubai, UAE',
      color: '#3498DB'
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone',
      content: '+971 563 528 126',
      link: 'tel:+971563528126',
      color: '#27AE60'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      content: 'groupsamrelocate@gmail.com',
      link: 'mailto:groupsamrelocate@gmail.com',
      color: '#5DADE2'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Business Hours',
      content: 'Monday - Friday: 8:00 AM - 7:00 PM\nSaturday & Sunday: Closed',
      color: '#D4AF37'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center text-white pt-20" data-testid="contact-hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920" 
            alt="Dubai"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#27AE60]" />
              <span className="text-[#27AE60] font-semibold tracking-[0.2em] uppercase text-sm">Get in Touch</span>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#27AE60]" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-testid="contact-hero-title">
              Contact <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#27AE60] to-[#3498DB]">Us</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Let's discuss how we can help you achieve your goals in the UAE
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl font-bold mb-8 text-white" data-testid="contact-info-heading">
                  Contact Information
                </h2>

                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <motion.div 
                      key={index}
                      className="flex items-start gap-4 group"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div 
                        className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                        style={{ background: `${info.color}15`, color: info.color }}
                      >
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{info.title}</h3>
                        {info.link ? (
                          <a href={info.link} className="text-gray-400 hover:text-white transition-colors whitespace-pre-line">
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-400 whitespace-pre-line">{info.content}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Quick Contact Buttons */}
                <div className="mt-10 flex flex-col gap-4">
                  <a
                    href="https://wa.me/971563528126?text=Hello%20SAM%20Relocation%20Group"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(37,211,102,0.4)] transition-all duration-300"
                  >
                    <MessageCircle size={20} />
                    WhatsApp Us
                  </a>
                </div>

                {/* Map */}
                <div className="mt-10 rounded-3xl overflow-hidden border border-white/10">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.1702983933856!2d55.28236!3d25.22374!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f433e6f8e5c5d%3A0x5c5e6f8e5c5d5e6f!2sDubai%20World%20Trade%20Centre!5e0!3m2!1sen!2sae!4v1234567890"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title="SAM Relocation Group Location"
                    data-testid="contact-map"
                  ></iframe>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 md:p-10"
                data-testid="contact-form-container"
              >
                <div className="absolute top-0 right-0 w-60 h-60 bg-[#3498DB]/10 rounded-full blur-3xl" />
                
                <h2 className="text-3xl font-bold mb-2 text-white relative z-10" data-testid="form-heading">
                  Send Us a Message
                </h2>
                <p className="text-gray-400 mb-8 relative z-10">
                  Fill out the form below and we'll get back to you shortly.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6 relative z-10" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-white font-medium mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#3498DB] focus:ring-[#3498DB]"
                        placeholder="John Doe"
                        data-testid="form-input-name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-white font-medium mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#3498DB] focus:ring-[#3498DB]"
                        placeholder="john@example.com"
                        data-testid="form-input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-white font-medium mb-2 block">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#3498DB] focus:ring-[#3498DB]"
                        placeholder="+971 XX XXX XXXX"
                        data-testid="form-input-phone"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company" className="text-white font-medium mb-2 block">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#3498DB] focus:ring-[#3498DB]"
                        placeholder="Your Company"
                        data-testid="form-input-company"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-white font-medium mb-2 block">
                      Service of Interest *
                    </Label>
                    <select
                      id="service"
                      name="service"
                      required
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full h-12 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:border-[#3498DB] focus:ring-2 focus:ring-[#3498DB] focus:outline-none"
                      data-testid="form-select-service"
                    >
                      <option value="" className="bg-[#0a0a0a]">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service} className="bg-[#0a0a0a]">
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white font-medium mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#3498DB] focus:ring-[#3498DB]"
                      placeholder="Tell us about your needs..."
                      data-testid="form-textarea-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white text-lg font-semibold rounded-full hover:shadow-[0_0_30px_rgba(52,152,219,0.4)] transition-all duration-300"
                    data-testid="form-submit-button"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-20 relative overflow-hidden" data-testid="quick-contact-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#111] to-[#0a0a0a]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#27AE60] rounded-full filter blur-[200px] opacity-10" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3498DB] rounded-full filter blur-[200px] opacity-10" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Prefer to speak directly?
            </h3>
            <p className="text-gray-400 mb-8">
              Our team is available during business hours to answer your questions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+971563528126"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(52,152,219,0.4)] transition-all duration-300"
                data-testid="quick-contact-call-button"
              >
                <Phone size={20} />
                Call Us Now
              </a>
              <a
                href="https://wa.me/971563528126?text=Hello%20SAM%20Relocation%20Group"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-white/20 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300"
                data-testid="quick-contact-whatsapp-button"
              >
                <MessageCircle size={20} />
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
